import { CostDuration, PricingUnit } from "@/types";
import { ColumnDef } from "@tanstack/react-table";
import {
    expr,
    makeCellWithRegexSorter,
    regex,
    transformAllDataTables,
} from "./shared";
import exprCompiler from "@/utils/expr";
import RegionLinkPreloader from "@/components/RegionLinkPreloader";

export interface GCPPlatformPricing {
    ondemand?: string | number;
    spot?: string | number;
    cud_1yr?: string | number;
    cud_3yr?: string | number;
}

export interface GCPPricing {
    [region: string]: {
        [platform: string]: GCPPlatformPricing;
    };
}

export type GCPInstance = {
    instance_type: string;
    pretty_name: string;
    family: string;
    vCPU: number;
    memory: number;
    GPU: number;
    GPU_model?: string;
    GPU_memory?: number;
    pricing: GCPPricing;
    regions: Record<string, string>;
    network_performance: string;
    generation: string;
    local_ssd: boolean;
    local_ssd_size?: number;
    shared_cpu: boolean;
    compute_optimized?: boolean;
    memory_optimized?: boolean;
    accelerator_type?: string;
};

const initialColumnsArr = [
    ["pretty_name", true],
    ["instance_type", true],
    ["memory", true],
    ["vCPU", true],
    ["memory_per_vcpu", false],
    ["GPU", false],
    ["network_performance", false],
    ["generation", false],
    ["local_ssd", false],
    ["shared_cpu", false],
    ["linux-ondemand", true],
    ["linux-1yr-cud", true],
    ["linux-3yr-cud", true],
    ["linux-spot", true],
    ["windows-ondemand", true],
    ["windows-spot", true],
] as const;

export const initialColumnsValue: {
    [idx in (typeof initialColumnsArr)[number][0]]: boolean;
} = {} as any;

for (const [key, value] of initialColumnsArr) {
    initialColumnsValue[key] = value;
}

export function transformDataTables(dataTablesData: any) {
    return transformAllDataTables(initialColumnsArr, dataTablesData);
}

export function makePrettyNames<V>(
    makeColumnOption: (
        key: keyof typeof initialColumnsValue,
        label: string,
    ) => V,
) {
    return [
        makeColumnOption("pretty_name", "Instance Name"),
        makeColumnOption("instance_type", "API Name"),
        makeColumnOption("memory", "Instance Memory"),
        makeColumnOption("vCPU", "vCPUs"),
        makeColumnOption("memory_per_vcpu", "Memory per vCPU"),
        makeColumnOption("GPU", "GPUs"),
        makeColumnOption("network_performance", "Network Performance"),
        makeColumnOption("generation", "Generation"),
        makeColumnOption("local_ssd", "Local SSD"),
        makeColumnOption("shared_cpu", "Shared CPU"),
        makeColumnOption("linux-ondemand", "Linux On-Demand"),
        makeColumnOption("linux-1yr-cud", "Linux 1-Year CUD"),
        makeColumnOption("linux-3yr-cud", "Linux 3-Year CUD"),
        makeColumnOption("linux-spot", "Linux Spot"),
        makeColumnOption("windows-ondemand", "Windows On-Demand"),
        makeColumnOption("windows-spot", "Windows Spot"),
    ];
}

function round(value: number) {
    return Math.round(value * 100) / 100;
}

function calculateCost(
    price: string | number | undefined,
    instance: GCPInstance,
    pricingUnit: PricingUnit,
    costDuration: CostDuration,
    usdRate: number,
) {
    if (price === undefined || price === null || price === "") return -1;

    const hourMultipliers = {
        secondly: 1 / 3600,
        minutely: 1 / 60,
        hourly: 1,
        daily: 24,
        weekly: 168,
        monthly: (365 * 24) / 12,
        annually: 365 * 24,
    };

    const divisor =
        pricingUnit === "vcpu"
            ? instance.vCPU
            : pricingUnit === "memory"
              ? instance.memory
              : 1;
    if (!divisor) return -1;

    return (Number(price) * hourMultipliers[costDuration] * usdRate) / divisor;
}

export function calculateAndFormatCost(
    price: string | number | undefined,
    instance: GCPInstance,
    pricingUnit: PricingUnit,
    costDuration: CostDuration,
    currency: { code: string; usdRate: number },
) {
    const cost = calculateCost(
        price,
        instance,
        pricingUnit,
        costDuration,
        currency.usdRate,
    );
    if (cost < 0 || !Number.isFinite(cost)) return "unavailable";

    const maximumFractionDigits =
        costDuration === "secondly" || costDuration === "minutely" ? 6 : 4;
    const durationLabel =
        costDuration === "secondly"
            ? "per sec"
            : costDuration === "minutely"
              ? "per min"
              : costDuration;
    const unitLabel =
        pricingUnit === "instance"
            ? ""
            : pricingUnit === "vcpu"
              ? " / vCPU"
              : " / GiB";

    const formatted = Intl.NumberFormat("en-US", {
        style: "currency",
        currency: currency.code,
        maximumFractionDigits,
    }).format(cost);
    return `${formatted} ${durationLabel}${unitLabel}`;
}

function getPricingColumn(
    selectedRegion: string,
    pricingUnit: PricingUnit,
    costDuration: CostDuration,
    getter: (
        pricing: GCPPricing[string] | undefined,
    ) => string | number | undefined,
    currency: { code: string; usdRate: number },
) {
    const getCost = (instance: GCPInstance) =>
        calculateCost(
            getter(instance.pricing?.[selectedRegion]),
            instance,
            pricingUnit,
            costDuration,
            currency.usdRate,
        );

    return {
        sortingFn: (rowA, rowB) =>
            getCost(rowA.original) - getCost(rowB.original),
        sortUndefined: "last" as const,
        accessorFn: (instance: GCPInstance) => {
            const cost = getCost(instance);
            return cost < 0 || !Number.isFinite(cost) ? undefined : cost;
        },
        ...makeCellWithRegexSorter("pricing", (info) =>
            calculateAndFormatCost(
                getter(info.row.original.pricing?.[selectedRegion]),
                info.row.original,
                pricingUnit,
                costDuration,
                currency,
            ),
        ),
    } satisfies Partial<ColumnDef<GCPInstance>>;
}

export const columnsGen = (
    selectedRegion: string,
    pricingUnit: PricingUnit,
    costDuration: CostDuration,
    _reservedTerm: string,
    currency: {
        code: string;
        usdRate: number;
        cnyRate: number;
    },
): ColumnDef<GCPInstance>[] => [
    {
        accessorKey: "pretty_name",
        id: "pretty_name",
        header: "Instance Name",
        sortingFn: "alphanumeric",
        filterFn: regex({ accessorKey: "pretty_name" }),
    },
    {
        accessorKey: "instance_type",
        id: "instance_type",
        header: "API Name",
        sortingFn: "alphanumeric",
        cell: (info) => {
            const value = info.getValue() as string;
            return (
                <RegionLinkPreloader
                    onClick={(event) => event.stopPropagation()}
                    href={`/gcp/${value}`}
                >
                    {value}
                </RegionLinkPreloader>
            );
        },
        filterFn: regex({ accessorKey: "instance_type" }),
    },
    {
        accessorKey: "memory",
        id: "memory",
        header: "Instance Memory",
        filterFn: expr,
        cell: (info) => `${info.getValue() as number} GiB`,
    },
    {
        accessorKey: "vCPU",
        id: "vCPU",
        header: "vCPUs",
        filterFn: expr,
    },
    {
        accessorKey: "memory",
        id: "memory_per_vcpu",
        header: "Memory per vCPU",
        filterFn: (row, _, filterValue) => {
            const value = round(row.original.memory / row.original.vCPU);
            try {
                return exprCompiler(filterValue)(value, `${value} GiB/vCPU`);
            } catch {
                return true;
            }
        },
        cell: (info) =>
            `${round(
                (info.getValue() as number) / info.row.original.vCPU,
            )} GiB/vCPU`,
    },
    {
        accessorKey: "GPU",
        id: "GPU",
        header: "GPUs",
        filterFn: expr,
    },
    {
        accessorKey: "network_performance",
        id: "network_performance",
        header: "Network Performance",
        filterFn: regex({ accessorKey: "network_performance" }),
    },
    {
        accessorKey: "generation",
        id: "generation",
        header: "Generation",
        filterFn: regex({ accessorKey: "generation" }),
    },
    {
        accessorKey: "local_ssd",
        id: "local_ssd",
        header: "Local SSD",
        cell: (info) => ((info.getValue() as boolean) ? "Yes" : "No"),
    },
    {
        accessorKey: "shared_cpu",
        id: "shared_cpu",
        header: "Shared CPU",
        cell: (info) => ((info.getValue() as boolean) ? "Yes" : "No"),
    },
    {
        id: "linux-ondemand",
        header: "Linux On Demand cost",
        ...getPricingColumn(
            selectedRegion,
            pricingUnit,
            costDuration,
            (pricing) => pricing?.linux?.ondemand,
            currency,
        ),
    },
    {
        id: "linux-1yr-cud",
        header: "Linux 1-Year CUD cost",
        ...getPricingColumn(
            selectedRegion,
            pricingUnit,
            costDuration,
            (pricing) => pricing?.linux?.cud_1yr,
            currency,
        ),
    },
    {
        id: "linux-3yr-cud",
        header: "Linux 3-Year CUD cost",
        ...getPricingColumn(
            selectedRegion,
            pricingUnit,
            costDuration,
            (pricing) => pricing?.linux?.cud_3yr,
            currency,
        ),
    },
    {
        id: "linux-spot",
        header: "Linux Spot cost",
        ...getPricingColumn(
            selectedRegion,
            pricingUnit,
            costDuration,
            (pricing) => pricing?.linux?.spot,
            currency,
        ),
    },
    {
        id: "windows-ondemand",
        header: "Windows On Demand cost",
        ...getPricingColumn(
            selectedRegion,
            pricingUnit,
            costDuration,
            (pricing) => pricing?.windows?.ondemand,
            currency,
        ),
    },
    {
        id: "windows-spot",
        header: "Windows Spot cost",
        ...getPricingColumn(
            selectedRegion,
            pricingUnit,
            costDuration,
            (pricing) => pricing?.windows?.spot,
            currency,
        ),
    },
];
